import { useEffect, useMemo, useReducer, useRef } from "react";
import {
	type TChatPlaybackEffect,
	type TChatPlaybackEffectIntent,
	type TChatPlaybackEvent,
	type TChatPlaybackMessage,
	type TChatPlaybackState,
	initialChatPlaybackState,
	reduceChatPlayback,
	selectIsTyping,
	selectTypingMessage,
	selectVisibleMessages,
} from "./chat-playback";

type TUseChatPlaybackOptions = {
	messages: readonly TChatPlaybackMessage[];
	active: boolean;
	revealAll: boolean;
	resetKey?: string | number;
};

type TUseChatPlaybackResult = {
	state: TChatPlaybackState;
	visibleMessages: TChatPlaybackMessage[];
	isTyping: boolean;
	typingMessage: TChatPlaybackMessage | undefined;
	isComplete: boolean;
};

type TPlaybackReducerState = {
	state: TChatPlaybackState;
	effect: TChatPlaybackEffect;
};

const EVENT_BY_INTENT: Record<TChatPlaybackEffectIntent, TChatPlaybackEvent["type"]> = {
	"pre-message": "PRE_MESSAGE_DELAY_ELAPSED",
	typing: "TYPING_ELAPSED",
	streaming: "STREAM_ELAPSED",
};

export function useChatPlayback({
	messages,
	active,
	revealAll,
	resetKey,
}: TUseChatPlaybackOptions): TUseChatPlaybackResult {
	const messagesRef = useRef(messages);
	messagesRef.current = messages;

	const [{ state, effect }, dispatch] = useReducer(
		(current: TPlaybackReducerState, event: TChatPlaybackEvent): TPlaybackReducerState =>
			reduceChatPlayback(current.state, event, messagesRef.current),
		{ state: initialChatPlaybackState, effect: { type: "none" } },
	);

	const timerRef = useRef<number | null>(null);

	const clearTimer = () => {
		if (timerRef.current !== null) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	useEffect(() => clearTimer, []);

	useEffect(() => {
		clearTimer();
		dispatch({ type: "RESET" });
	}, [resetKey]);

	useEffect(() => {
		clearTimer();
		if (revealAll) {
			dispatch({ type: "REVEAL_ALL" });
			return;
		}
		if (active) {
			dispatch({ type: "ACTIVATE" });
		}
		return clearTimer;
	}, [active, revealAll]);

	useEffect(() => {
		if (effect.type !== "schedule") return;

		timerRef.current = window.setTimeout(() => {
			timerRef.current = null;
			dispatch({ type: EVENT_BY_INTENT[effect.intent] });
		}, effect.delayMs);

		return clearTimer;
	}, [effect, state.phase]);

	const visibleMessages = useMemo(
		() => selectVisibleMessages(messages, state.visibleMessageIds),
		[messages, state.visibleMessageIds],
	);

	const typingMessage = useMemo(() => selectTypingMessage(messages, state), [messages, state]);

	return {
		state,
		visibleMessages,
		isTyping: selectIsTyping(state),
		typingMessage,
		isComplete: state.phase === "complete",
	};
}
